import React, { useEffect } from 'react';
import { css } from '@emotion/css';
import Button from '../../../../components/general/Button';
import ConfirmationDialog from '../../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../../components/general/WarningConfirmation';
import { color_shades_dark, color_variants_shadow_10, dark_grey_blue_70 } from '../../../../constants/colors';
import ApplicationModel from '../../../../models/applications/api/ApplicationModel';
import { breakpoint_small } from '../../../../constants/breakpoints';
import { getSparkMainBaseUrl } from '../../../../constants/Urls';
import { getSquareConnectedApplications } from '../../../../api/controllers/businessManagement';
import ConnectedAppResponse from '../../../../models/integrations/api/Salesforce/ConnectedAppResponse';

const infoIcon = require('../../../../content/images/info_icon.svg');

interface SquareIntegrationProps {
    applications: ApplicationModel[];
    isLoading: boolean;
    organizationId: string;
    organizationName?: string;
}

const SquareIntegration: React.FC<SquareIntegrationProps> = ({ applications, isLoading, organizationId, organizationName }) => {
    const isProductionEnv = process.env.VOICIFY_ENVIRONMENT === 'production';
    const squareEnvironment = isProductionEnv ? 'production' : 'sandbox';
    const squareClientId = isProductionEnv ? 'sq0idp-4jbtIv9S98iE9LtKLBa96g' : 'sandbox-sq0idb-nPdl4MhfiKs95HSfLXiA_Q';
    const squareClientSecret = 'none';

    const [showSquareModal, setShowSquareModal] = React.useState(false);
    const [connectedApps, setConnectedApps] = React.useState<ConnectedAppResponse[]>([]);
    const [isLoadingConnectedApps, setIsLoadingConnectedApps] = React.useState(false);

    useEffect(() => {
        const fetchConnectedApps = async () => {
            if (!organizationId || !applications.length) return;

            setIsLoadingConnectedApps(true);
            try {
                const applicationIds = applications
                    .map(app => app.id)
                    .filter(id => id && id.trim());

                const result = await getSquareConnectedApplications(organizationId, applicationIds);

                if (result.resultType === "Ok" && result.data) {
                    setConnectedApps(result.data);
                }
            } catch (error) {
                console.error('Error fetching connected applications:', error);
            }
            setIsLoadingConnectedApps(false);
        };

        fetchConnectedApps();
    }, [organizationId, applications]);

    const getRedirectUri = () => {
        return `${getSparkMainBaseUrl()}/v/orgSettings/integrations/square/oauth/callback`;
    };

    const handleSquareConnect = () => {
        const redirectUri = encodeURIComponent(getRedirectUri());
        const clientId = squareClientId;
        const scope = 'ITEMS_READ+ITEMS_WRITE+MERCHANT_PROFILE_READ+ORDERS_READ+ORDERS_WRITE+PAYMENTS_READ+PAYMENTS_WRITE+CUSTOMERS_READ+INVOICES_WRITE';
        const environment = squareEnvironment;

        // Generate state token with structure: Square-<organizationId>-id-<clientId>-secret-<clientSecret>-env-<environment>-orgName-<organizationName>
        const state = `Square-${organizationId}-id-${clientId}-secret-${squareClientSecret}-env-${environment}-orgName-${encodeURIComponent(organizationName || '')}`;

        const baseUrl = environment === 'production'
            ? 'https://connect.squareup.com/oauth2/authorize'
            : 'https://connect.squareupsandbox.com/oauth2/authorize';

        window.location.href = `${baseUrl}?response_type=code&client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&state=${state}`;
    };

    const isConnected = connectedApps.length > 0;

    return (
        <>
            <div className={integrationCardStyle}>
                <div className={integrationCardContentStyle}>
                    <div className={headerStyle}>
                        <h2>Square Integration</h2>
                    </div>
                    <p>Square is a financial services and mobile payment company that enables business merchant service aggregation and mobile payments.</p>
                    <p>Connect to Square to give your assistant access to products, merchant profiles, orders, and payment capabilities.</p>

                    {isConnected && (
                        <div style={{ marginTop: '16px', color: '#0066cc', fontWeight: 600 }}>
                            ACCOUNT CONNECTED (All Locations)
                        </div>
                    )}
                </div>
                <Button
                    themes={["primary"]}
                    text="Authenticate"
                    className={authenticateButtonStyle(false)}
                    onClick={() => setShowSquareModal(true)}
                />
            </div>
            {showSquareModal && (
                <ConfirmationDialog
                    title="Connect to Square"
                    onClose={() => setShowSquareModal(false)}
                    onConfirm={handleSquareConnect}
                    deleteText="Authenticate"
                >
                    <WarningConfirmation
                        showImage={false}
                        text="By authenticating to Square, you grant your voice assistant access to read and write items, orders, merchant profile, and payments. This helps provide seamless payment and catalog interactions."
                    />
                </ConfirmationDialog>
            )}
        </>
    );
};

const integrationCardStyle = css`
    border: 1px solid ${color_shades_dark};
    display: flex;
    margin-top: 10px;
    justify-content: space-between;
    border-radius: 8px;
    padding: 24px;
    background: white;
    box-shadow: 0 2px 4px 0 ${color_variants_shadow_10};
    h2 {
        margin-bottom: 16px;
        font-size: 16px;
        font-weight: 600;
    }
    
    p {
        margin-bottom: 6px;
        font-size: 14px;
        color: ${dark_grey_blue_70};
    }

    ${breakpoint_small} {
        margin-top: 5px;
        display: block;
    }
`;

const integrationCardContentStyle = css`
    width: 50%;
    ${breakpoint_small} {
        width: 100%;
    }
`;

const authenticateButtonStyle = (disabled: boolean) => css`
    align-self: center;
    ${disabled && css`
        opacity: 0.5;
        cursor: not-allowed;
    `}
`;

const headerStyle = css`
    display: flex;
    align-items: center;
    gap: 0px;
    margin-bottom: 16px;
`;

export default SquareIntegration;
